import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Atg = { id: string; label: string; archivedAt?: string | null };
type Site = {
  id: string; name: string; customer?: string; address?: string;
  archived_at?: string | null;
  atgs: Atg[];
};

export function Sites() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [sites, setSites] = useState<Site[]>([]);
  const [name, setName] = useState('');
  const [customer, setCustomer] = useState('');
  const [address, setAddress] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  // Delete modal state.
  const [deleteTarget, setDeleteTarget] = useState<Site | null>(null);
  const [deleteMode, setDeleteMode] = useState<'archive' | 'purge'>('archive');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  async function refresh() {
    const qs = includeArchived ? '?includeArchived=1' : '';
    setSites(await api.get(`/api/sites${qs}`));
  }
  useEffect(() => { refresh().catch(() => {}); }, [includeArchived]);

  async function addSite() {
    await api.post('/api/sites', { name, customer, address });
    setName(''); setCustomer(''); setAddress('');
    await refresh();
  }

  function openDelete(s: Site) {
    setDeleteTarget(s);
    setDeleteMode('archive');
    setDeleteConfirm('');
    setDeleteErr('');
  }
  function closeDelete() {
    setDeleteTarget(null);
    setDeleteConfirm('');
    setDeleteErr('');
    setDeleting(false);
  }
  async function confirmDelete() {
    if (!deleteTarget) return;
    // Purge requires typing the site name as an additional safety check.
    if (deleteMode === 'purge' && deleteConfirm !== deleteTarget.name) {
      setDeleteErr(`Type the site name exactly to confirm purge.`);
      return;
    }
    setDeleting(true); setDeleteErr('');
    try {
      const r = await fetch(`/api/sites/${deleteTarget.id}?mode=${deleteMode}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || r.statusText);
      }
      closeDelete();
      await refresh();
    } catch (e: any) {
      setDeleteErr(e.message || 'Delete failed');
      setDeleting(false);
    }
  }

  async function unarchive(siteId: string) {
    await api.post(`/api/sites/${siteId}/unarchive`, {});
    await refresh();
  }

  return (
    <>
      <div className="card">
        <h2>Add site</h2>
        <div className="row">
          <div className="col"><label>Name</label><input value={name} onChange={e => setName(e.target.value)} /></div>
          <div className="col"><label>Customer</label><input value={customer} onChange={e => setCustomer(e.target.value)} /></div>
          <div className="col"><label>Address</label><input value={address} onChange={e => setAddress(e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 12 }}>
          <button disabled={!name} onClick={addSite}>Create site</button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>All sites</h2>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} />
            Show archived
          </label>
        </div>
        <div className="table-wrap"><table style={{ marginTop: 12 }}>
          <thead><tr><th>Name</th><th>Customer</th><th>Address</th><th>ATGs</th><th>Status</th><th /></tr></thead>
          <tbody>
            {sites.map(s => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>{s.customer || '—'}</td>
                <td>{s.address || '—'}</td>
                <td>{s.atgs?.length || 0}</td>
                <td>
                  {s.archived_at
                    ? <span className="pill" style={{ background: '#999' }}>Archived</span>
                    : <span className="pill ok">Live</span>}
                </td>
                <td style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Link to={`/sites/${s.id}`}>Open →</Link>
                  {isAdmin && s.archived_at && (
                    <button onClick={() => unarchive(s.id)} style={{ padding: '2px 8px', fontSize: 13 }}>Unarchive</button>
                  )}
                  {isAdmin && !s.archived_at && (
                    <button onClick={() => openDelete(s)}
                            style={{ padding: '2px 8px', fontSize: 13, background: '#c44', color: '#fff' }}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!sites.length && <tr><td colSpan={6} style={{ color: 'var(--muted)' }}>No sites.</td></tr>}
          </tbody>
        </table></div>
      </div>

      {deleteTarget && (
        <DeleteModal
          site={deleteTarget}
          mode={deleteMode}
          setMode={setDeleteMode}
          confirmText={deleteConfirm}
          setConfirmText={setDeleteConfirm}
          deleting={deleting}
          err={deleteErr}
          onCancel={closeDelete}
          onConfirm={confirmDelete}
        />
      )}
    </>
  );
}

function DeleteModal(props: {
  site: Site;
  mode: 'archive' | 'purge';
  setMode: (m: 'archive' | 'purge') => void;
  confirmText: string;
  setConfirmText: (s: string) => void;
  deleting: boolean;
  err: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { site, mode, setMode, confirmText, setConfirmText, deleting, err, onCancel, onConfirm } = props;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div className="card" style={{ maxWidth: 560, width: '90%' }}>
        <h2 style={{ marginTop: 0 }}>Delete site: {site.name}</h2>
        <p style={{ color: 'var(--muted)' }}>
          This site has <b>{site.atgs?.length || 0}</b> ATG console{(site.atgs?.length || 0) === 1 ? '' : 's'}.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="radio" checked={mode === 'archive'} onChange={() => setMode('archive')} style={{ marginTop: 4 }} />
            <div>
              <div style={{ fontWeight: 600 }}>Archive (preserve history)</div>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                Hide this site from the dashboard and stop polling its ATGs. All tank readings,
                deliveries, and sensor history remain queryable in the Reports page via the
                "Include archived" toggle. Reversible.
              </div>
            </div>
          </label>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="radio" checked={mode === 'purge'} onChange={() => setMode('purge')} style={{ marginTop: 4 }} />
            <div>
              <div style={{ fontWeight: 600, color: '#c44' }}>Purge (delete everything)</div>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                Permanently delete the site, its ATGs, and <b>all</b> historical
                tank readings, deliveries, sensor readings, and poll logs. This cannot be undone.
              </div>
            </div>
          </label>
        </div>

        {mode === 'purge' && (
          <div style={{ marginTop: 16, padding: 12, background: 'rgba(220,80,80,0.1)', borderRadius: 4 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
              Type <b>{site.name}</b> to confirm purge:
            </label>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
                   placeholder={site.name} style={{ width: '100%' }} />
          </div>
        )}

        {err && <div style={{ color: 'crimson', marginTop: 12 }}>{err}</div>}

        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={deleting}>Cancel</button>
          <button onClick={onConfirm} disabled={deleting}
                  style={{ background: mode === 'purge' ? '#c44' : '#2e7df6', color: '#fff' }}>
            {deleting ? 'Working…' : mode === 'archive' ? 'Archive site' : 'Purge site forever'}
          </button>
        </div>
      </div>
    </div>
  );
}
